import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Drawer.css';
import { useContext } from '../Context';

const SideDrawer = () => {
    const { globalState, setGlobalState } = useContext();
    const { drawerOpen, library, devMode, limit, isMatchLoading } = globalState; // Include 'limit' from globalState
    const drawerRef = useRef(null);

    const handleToggle = () => {
        setGlobalState(prevState => ({
            ...prevState,
            devMode: !prevState.devMode
        }));
    };

    const handleToggleLoading = () => {
        setGlobalState(prevState => ({
            ...prevState,
            isMatchLoading: !prevState.isMatchLoading
        }));
    };

    const [isLocalLoading, setIsLocalLoading] = useState(false);
    const [visibleItems, setVisibleItems] = useState([]);
    const [itemsToShow, setItemsToShow] = useState(limit || 50); // Initialize with limit from globalState

    useEffect(() => {
        if (library.length > 0) {
            setVisibleItems(library.slice(0, itemsToShow));
        }
    }, [library, itemsToShow]);

    const loadMoreItems = () => {
        setIsLocalLoading(true);
        setTimeout(() => {
            setVisibleItems(prevItems => [
                ...prevItems,
                ...library.slice(prevItems.length, prevItems.length + 50)
            ]);
            setIsLocalLoading(false);
        }, 500); // Simulate loading delay
    };

    const handleScroll = useCallback(() => {
        if (drawerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = drawerRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 5) {
                loadMoreItems();
            }
        }
    }, [drawerRef, loadMoreItems]);

    useEffect(() => {
        if (drawerRef.current) {
            drawerRef.current.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (drawerRef.current) {
                drawerRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll]);

    useEffect(() => {
        const handleClickOutside = event => {
            if (drawerRef.current && !drawerRef.current.contains(event.target)) {
                setGlobalState(prevState => ({
                    ...prevState,
                    drawerOpen: false
                }));
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setGlobalState]);

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setGlobalState(prevState => ({
            ...prevState,
            limit: newLimit
        }));
    };

    const alertProductIds = () => {
        const productIds = library.map(product => product.product_id).join(', ')
        const blob = new Blob([productIds], { type: 'text/plain' })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = 'product_ids.txt'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        URL.revokeObjectURL(url)
    }

    const simulateInstallPrompt = () => {
        const fakeEvent = {
            prompt: () => {
                alert('Simulated Install Prompt Triggered!');
            },
            userChoice: new Promise((resolve) => {
                setTimeout(() => {
                    resolve({ outcome: 'accepted' }); // Simulate user acceptance
                }, 1000);
            }),
        };

        window.dispatchEvent(new CustomEvent('beforeinstallprompt', { detail: fakeEvent }));
    };

    return (
        <div ref={drawerRef} className={`drawer position-relative ${drawerOpen ? 'open' : ''}`}>
            {isLocalLoading && (
                <div className='loading-overlay'>
                    <div className='spinner'></div>
                    <p>Loading...</p>
                </div>
            )}

            <div className="toggle-container mb1">
                <label className="toggle-label">
                    Developer Mode
                </label>
                <label className="switch">
                    <input
                        type="checkbox"
                        checked={devMode}
                        onChange={handleToggle}
                    />
                    <span className="slider"></span>
                </label>
            </div>

            <div className="toggle-container mb1">
                <label className="toggle-label">
                    Simulate Loading
                </label>
                <label className="switch">
                    <input
                        type="checkbox"
                        checked={isMatchLoading}
                        onChange={handleToggleLoading}
                    />
                    <span className="slider"></span>
                </label>
            </div>

            {devMode && (
                <>
                    <div className='buttons'>
                        <button onClick={alertProductIds} className="mb1 alert-button">
                            Download Product IDs
                        </button>

                        {/* Add a number field to adjust the limit */}
                        <div className="limit-input">
                            <label htmlFor="limit">Set Limit:</label>
                            <input
                                type="number"
                                id="limit"
                                value={limit}
                                onChange={handleLimitChange}
                            />
                        </div>
                    </div>

                    <div>
                        <button onClick={simulateInstallPrompt}>Simulate Install Prompt</button>
                    </div>
                </>
            )}

            <a className='cursor-pointer mt1 display-block' onClick={() => setGlobalState({ ...globalState, drawerOpen: false })}>close</a>
        </div>
    );
};

export default SideDrawer;
